import React, { type JSX } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { StringListEditor } from "./translations-section";

/**
 * The four rows, in the order the show pages print them
 * (`src/components/show/enrichment.tsx`).
 */
const TRANSLITERATION_ROWS = [
  { key: "en", label: "English" },
  { key: "el", label: "Greek" },
  { key: "ar", label: "Arabic" },
  { key: "ro", label: "Romanian" },
] as const;

export interface TransliterationsSectionProps {
  value: Record<string, string[]> | undefined;
  onChange: (next: Record<string, string[]>) => void;
  disabled?: boolean;
}

/** The row's chips, dropping anything a nullish-laden document left behind. */
function stringsOf(
  value: Record<string, string[]> | undefined,
  key: string,
): string[] {
  const items = value?.[key];
  if (!Array.isArray(items)) return [];
  return items.filter((item): item is string => typeof item === "string");
}

/**
 * The transliterations card: four fixed rows, always all four.
 *
 * `TransliterationsDbDto`'s four members are `required` on seven of the eight
 * entities, so the save must carry every key — an edit writes all four, missing
 * ones as `[]`, and a key this section does not own rides along untouched.
 *
 * The section is part of the enrichment draft, not a translation: a
 * transliterations-only edit marks the enrichment dirty. Whether it renders at
 * all is the parent's call (`hasTransliterations`), not this section's.
 */
export function TransliterationsSection({
  value,
  onChange,
  disabled,
}: TransliterationsSectionProps): JSX.Element {
  const emit = (key: string, next: string[]) => {
    const merged: Record<string, string[]> = {};
    for (const row of TRANSLITERATION_ROWS) {
      merged[row.key] = row.key === key ? next : stringsOf(value, row.key);
    }
    for (const [other, items] of Object.entries(value ?? {})) {
      if (!(other in merged)) merged[other] = items;
    }
    onChange(merged);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Transliterations</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {TRANSLITERATION_ROWS.map((row) => (
          <div key={row.key} className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">{row.label}</span>
            <StringListEditor
              value={stringsOf(value, row.key)}
              ariaLabel={row.label}
              disabled={disabled}
              onChange={(next) => emit(row.key, next)}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
